import * as QRCode from 'qrcode';
import { IOrderItem } from './interfaces/IOrderItem';
import { OptionType } from './enums/OptionType';
import { IOption } from './interfaces/IOption';
import { IOrderItemOption } from './interfaces/IOrderItemOption';
import { RoundMode } from './enums/RoundMode';
import axios from 'axios';
import { IDeliveryZone } from './interfaces/IDeliveryZone';
import polyIsClockwise from './plugins/polyIsClockwise';
import pointInPolygon from './plugins/pointInPolygon';
import { Spacetime } from 'spacetime';
import { ISimpleHourRange } from './interfaces/ISimpleHourRange';
import spacetime from 'spacetime';

export function loadImage(path: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    try {
      const img = new Image();
      // crossOrigin must be set before src so cross-origin images can be
      // drawn on an exportable canvas.
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        resolve(img);
      };
      img.onerror = () => {
        reject(new Error(`Could not load image: ${path}`));
      };
      img.src = path;
    } catch (e) {
      reject(e);
    }
  });
}

export function dataURLtoFile(dataurl, filename) {
  var arr = dataurl.split(','),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[arr.length - 1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  // Extension mapping for common MIME types
  const extensionMapping = {
    'image/jpeg': '.jpg',
    'image/png': '.png',
    'image/gif': '.gif',
    'image/webp': '.webp',
    'image/svg+xml': '.svg',
    'image/bmp': '.bmp',
  };

  // Get the extension for the current MIME type
  const extension = extensionMapping[mime];

  // Append the extension to the filename if it's not already present
  if (extension && !filename.endsWith(extension)) {
    filename += extension;
  }

  return new File([u8arr], filename, { type: mime });
}

export function normalizeTextBest(input: string): string {
  // Use NFD Unicode normalization to decompose characters into base characters and diacritical marks
  // Then remove the diacritical marks with a regex
  if (!input.normalize) return input;
  return input.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export function createQrImage(
  text: string,
  width: number,
  errorCorrectionLevel: 'L' | 'M' | 'Q' | 'H' = 'H'
): Promise<HTMLImageElement> {
  let opts = {
    errorCorrectionLevel,
    margin: 1,
    width,
  };
  return new Promise((resolve, reject) => {
    try {
      QRCode.toDataURL(text, opts, async (err, url) => {
        if (err) {
          reject(err);
          return;
        }

        try {
          resolve(await loadImage(url));
        } catch (loadError) {
          reject(loadError);
        }
      });
    } catch (e) {
      reject(e);
    }
  });
}

export function createQrBase64(text: string, width: number): Promise<string> {
  let opts = {
    errorCorrectionLevel: 'H',
    margin: 1,
    width,
  };
  return new Promise((resolve, reject) => {
    try {
      QRCode.toDataURL(text, opts, (err, url) => {
        if (err) {
          reject(err);
          return;
        }

        resolve(url);
      });
    } catch (e) {
      reject(e);
    }
  });
}

export function b64toBlob(b64Data, contentType = '', sliceSize = 512) {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
}

export function throttle(callback, limit) {
  let wait = false; // Initially, we're not waiting
  return () => {
    // We return a throttled function
    if (!wait) {
      // If we're not waiting
      callback.call(); // Execute users function
      wait = true; // Prevent future invocations
      setTimeout(() => {
        // After a period of time
        wait = false; // And allow future invocations
      }, limit);
    }
  };
}

export const debounce = (callback, wait = 250) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => callback(...args), wait);
  };
};

export function roundPrice(val, final = false) {
  /*if (val >= 100 && !final)
    return Math.round(val);
  else if (val >= 10)
    return Math.round((val + Number.EPSILON) * 10) / 10;
  else*/
  return Math.round((val + Number.EPSILON) * 100) / 100;
}

export function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function delay(time = 250) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, time);
  });
}

export function isNumeric(str: string) {
  if (typeof str != 'string') return false; // we only process strings!
  return (
    !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !isNaN(parseFloat(str))
  ); // ...and ensure strings of whitespace fail
}

type TypedArray =
  | Int8Array
  | Uint8Array
  | Int16Array
  | Uint16Array
  | Int32Array
  | Uint32Array
  | Uint8ClampedArray
  | Float32Array
  | Float64Array;
export function concatTypedArrays<T extends TypedArray>(a: T, b: T): T {
  // a, b TypedArray of same type
  // @ts-ignore
  const c = new a.constructor(a.length + b.length);
  c.set(a, 0);
  c.set(b, a.length);
  return c;
}

export function isDecimal(val) {
  // tslint:disable-next-line:no-bitwise
  return val !== val >> 0;
}

export function displayPrice(val, alwaysDecimals: boolean = false) {
  return alwaysDecimals || isDecimal(val) ? (+val).toFixed(2) : val;
}

export function bitCount(n) {
  // tslint:disable-next-line:no-bitwise
  n = n - ((n >> 1) & 0x55555555);
  // tslint:disable-next-line:no-bitwise
  n = (n & 0x33333333) + ((n >> 2) & 0x33333333);
  // tslint:disable-next-line:no-bitwise
  return (((n + (n >> 4)) & 0xf0f0f0f) * 0x1010101) >> 24;
}

export interface DisplayOption {
  compact: boolean;
  option: IOption;
  optionItems: IOrderItemOption[];
  description?: string;
}

/**
 * "3x " before an option item: always for Adicionales (`add`), and for any
 * other group once the quantity exceeds one (typed quantities, e.g. grams).
 */
export function getOptionQuantityPrefix(type: string, val: number): string {
  return type === OptionType.Add || val > 1 ? `${val}x ` : '';
}

export function getDisplayOptions(orderItems: IOrderItem[]) {
  const optionsByIndex: { [index: number]: DisplayOption[] } = {};

  let index = 0;
  for (const item of orderItems) {
    if (typeof item.index !== 'undefined') index = item.index + 1;
    else item.index = index++;

    if (!item.options || !item.options.length) continue;

    let options = optionsByIndex[item.index];
    if (!options) options = optionsByIndex[item.index] = [];

    for (const o of item.options) {
      let option: DisplayOption = options.find(
        (value) => value.option.id === o.optionItem.option.id
      );
      if (!option) {
        option = {
          option: o.optionItem.option,
          compact: false,
          optionItems: [],
        };
        option.option.optionItems = [];
        if (option.option.main) options.unshift(option);
        else options.push(option);
      }
      option.optionItems.push(o);
    }

    options.forEach((o) => {
      o.compact =
        o.option.type !== OptionType.Add &&
        !o.optionItems.some((value) => value.price > 0);
      if (o.compact) {
        const names = o.optionItems.map(
          (value) =>
            getOptionQuantityPrefix(o.option.type, value.val) +
            value.optionItem.title
        );
        o.description = names.join(', ');
      }
    });
  }

  return optionsByIndex;
}

export function round2(val) {
  return Math.round((val + Number.EPSILON) * 100) / 100;
}

export function applyRound(val: number, mode: RoundMode, interval: number) {
  if (mode === RoundMode.None || !interval) return val;

  let amount = val / interval;
  switch (mode) {
    case RoundMode.Nearest:
      amount = Math.round(amount);
      break;
    case RoundMode.Floor:
      amount = Math.floor(amount);
      break;
    case RoundMode.Ceil:
      amount = Math.ceil(amount);
      break;
  }
  return round2(amount * interval);
}

export function padLeft(str, padString, length) {
  while (str.length < length) str = padString + str;
  return str;
}

export function padNumber(val, length) {
  return padLeft(val + '', '0', length);
}

export function initArray(size: number, value: any) {
  const array = new Array(size);
  for (let i = 0; i < size; i++) array[i] = value;
  return array;
}

export function importScript(path: string) {
  return new Promise((resolve, reject) => {
    let script = document.createElement('script');
    script.onload = () => {
      resolve();
    };
    script.async = true;
    script.src = path;
    document.head.appendChild(script);
  });
}

export function notNull(first, second) {
  return first !== null ? first : second;
}

export function applyDiscount(val: number, discount: number) {
  if (typeof val === 'string') val = parseFloat(val);
  if (typeof discount === 'string') discount = parseFloat(discount);
  return roundPrice((val * (100 - parseFloat(discount + ''))) / 100);
}

export function calcDiscount(price, discountedPrice) {
  if (typeof price === 'string') price = parseFloat(price);
  if (typeof discountedPrice === 'string')
    discountedPrice = parseFloat(discountedPrice);

  return Math.round(Math.abs(100 * (1 - discountedPrice / price)));
}

export function rotateCanvas(angle, canvas, context) {
  return new Promise((resolve) => {
    // store current data to an image
    let myImage = new Image();
    myImage.src = canvas.toDataURL();

    myImage.onload = () => {
      // reset the canvas with new dimensions
      //canvas.width = ch;
      //canvas.height = cw;
      const cw = canvas.width,
        ch = canvas.height;

      context.save();
      // translate and rotate
      context.translate(cw, ch / cw);
      context.rotate(angle);
      // draw the previows image, now rotated
      context.drawImage(myImage, 0, 0);
      //context.restore();

      // clear the temporary image
      myImage = null;
      resolve();
    };
  });
}

export function arrayToPairs(array: any[]) {
  return array.reduce(function (result, value, index, array) {
    if (index % 2 === 0) result.push(array.slice(index, index + 2));
    return result;
  }, []);
}

export function coordsInsideZone(
  coords: [number, number],
  deliveryZones: IDeliveryZone[]
): IDeliveryZone | false {
  for (const zone of deliveryZones) {
    const poly = arrayToPairs(zone.points);

    const first = poly[0],
      last = poly[poly.length - 1];
    if (first[0] !== last[0] && first[1] !== last[1]) poly.push(first);

    if (!polyIsClockwise(poly)) poly.reverse();
    const res = pointInPolygon(coords, [poly]);
    if (res === true || res === 0) {
      return zone;
    }
  }
  return false;
}

export function getNow(timezone?: string): Spacetime {
  let d = spacetime.now(timezone);
  return d;
}

export function timeInsideHours(hours: ISimpleHourRange[], tz: string) {
  const now = getNow(tz);
  const timeNow = now.format('{hour-24-pad}:{minute-pad}');
  const dayNow = now.day();

  const ranges = hours.filter((h) => h.day === dayNow);

  if (!ranges) return false;

  for (const r of ranges) {
    if (timeNow >= r.from && timeNow <= r.to) return true;
  }

  return false;
}

export function mergeObjects(base: any, other: any) {
  return { ...base, ...other };
}

export function round(
  val: number,
  step: number,
  direction: 'up' | 'down' | 'round'
) {
  let q = val / step;

  switch (direction) {
    case 'round':
      q = Math.round(q);
      break;
    case 'up':
      q = Math.ceil(q);
      break;
    case 'down':
      q = Math.floor(q);
      break;
  }
  return q * step;
}

export function parseNumInput(input: string | number) {
  if (typeof input !== 'string') return input;
  else if (input.trim() === '') return null;
  return parseFloat(input.replace(',', '.').replace(' ', ''));
}

export const serializeQuery = (obj: {
  [key: string]: string | number | boolean | undefined | null;
}): string => {
  const str = [];
  for (const p in obj)
    if (Object.prototype.hasOwnProperty.call(obj, p)) {
      if (typeof obj[p] !== 'undefined' && obj[p] !== null)
        str.push(
          encodeURIComponent(p) + '=' + encodeURIComponent(obj[p] ?? '')
        );
    }
  return str.join('&');
};

export function extractBaseHref(defaultValue: string = '/'): string {
  if (!document) return defaultValue;

  const baseElement = document.querySelector('base');

  if (baseElement && baseElement.hasAttribute('href')) {
    const hrefValue = baseElement.getAttribute('href');
    if (hrefValue && hrefValue.trim() !== '') {
      // Remove leading and trailing slashes
      return hrefValue; //.replace(/^\/+|\/+$/g, '');
    }
  }

  return defaultValue;
}
